import { useEffect } from "react";

import {
  Box,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { Controller, useForm } from "react-hook-form";

import StyledDialog from "../../../../../common/components/dialog/StyledDialog";
import confirmDialogStyleOptions from "../../../../../common/helpers/confirmDialogParams";
import { useGetCustomer } from "../../../../../common/hooks/useGetCustomer";
import { skuCatalog } from "../../../../../common/models/enums";
import { DEFAULT_DEVICE_PASSWORD } from "../../../../../common/variables/common";
import { useCreateSKU } from "../hooks/useCreateSKU";
import {
  ALLOWED_MODELS_BY_TYPE,
  ISKUForm,
  defaultSKUForm,
} from "../variables/skuData";
import SKUFormItem from "./SKUFormItem";

interface CreateSKUDialogProps {
  open: boolean;
  handleClose: () => void;
}

const CreateSKUDialog = ({
  open,
  handleClose,
}: CreateSKUDialogProps): JSX.Element => {
  const confirm = useConfirm();
  const { createSKU, loading } = useCreateSKU();
  const { customer } = useGetCustomer();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ISKUForm>({
    defaultValues: defaultSKUForm,
    mode: "onBlur",
  });

  useEffect(() => {
    setValue("password", customer?.devicePassword || DEFAULT_DEVICE_PASSWORD);
  }, [customer?.devicePassword]);

  const watchedSKUNumber = watch("skuNumber");
  const watchedSKUData = watch("skuDataConfigurations");

  // When the SKU number changes, auto-populate skuDataConfigurations from the catalog
  const handleSKUNumberChange = (skuNumber: string) => {
    setValue("skuNumber", skuNumber);

    const catalogEntry = skuCatalog[skuNumber];

    if (!catalogEntry) return;

    // Auto-fill configuration name with product name (editable by user)
    setValue("configurationName", catalogEntry.productName);

    // Build one model-assignment entry per subcomponent.
    // AGX/POE nodes get an empty models array (they're processing nodes).
    const configurations = catalogEntry.subComponents.map(sub => ({
      name: sub.name,
      models:
        (ALLOWED_MODELS_BY_TYPE[sub.type] ?? []).length === 1
          ? // Auto-select the only option for single-model types (CH4, VOC, LWOP)
            [...(ALLOWED_MODELS_BY_TYPE[sub.type] ?? [])]
          : [],
    }));

    setValue("skuDataConfigurations", configurations);
  };

  const onSubmit = async (formData: ISKUForm) => {
    const catalogEntry = skuCatalog[formData.skuNumber];

    // Validate every non-AGX/POE component has at least one model
    const nonReadOnly = (formData.skuDataConfigurations || []).filter(
      (_cfg, i) => {
        const sub = catalogEntry?.subComponents[i];

        return sub && (ALLOWED_MODELS_BY_TYPE[sub.type] ?? []).length > 0;
      }
    );

    for (let i = 0; i < nonReadOnly.length; i++) {
      const cfg = nonReadOnly[i];

      if (!cfg.models || cfg.models.length === 0) {
        const sub = catalogEntry?.subComponents.find(s => s.name === cfg.name);

        import("../../../../../common/variables/notification").then(
          ({ errorNotification }) =>
            errorNotification(
              `${
                sub?.name ?? `Component ${i + 1}`
              }: At least one model is required`
            )
        );

        return;
      }
    }

    if (
      formData.password &&
      formData.password.length > 0 &&
      formData.password.length < 8
    ) {
      const { errorNotification } = await import(
        "../../../../../common/variables/notification"
      );

      errorNotification("Password must be at least 8 characters");

      return;
    }

    const { password, hasRecordVideo, defaultIp, ...skuFormData } = formData;

    await createSKU({
      ...skuFormData,
      // nodeCount comes from the catalog — not from user input
      nodeCount: catalogEntry?.nodeCount ?? 0,
      skuDataConfigurations: JSON.stringify(
        formData.skuDataConfigurations.map(device => ({
          ...device,
          hasRecordVideo,
          ...(password && { password }),
        }))
      ),
      ...(defaultIp?.trim() && { defaultIp: defaultIp.trim() }),
    });

    closeDialog();
  };

  const closeDialog = (): void => {
    reset();

    handleClose();
  };

  const confirmDialogClose = (): void => {
    confirm({
      title: "Are you sure you want to cancel?",
      confirmationText: "Cancel",
      cancellationText: "Back",
      ...confirmDialogStyleOptions,
      confirmationButtonProps: {
        ...confirmDialogStyleOptions.confirmationButtonProps,
        color: "primary",
      },
    })
      .then(async (): Promise<void> => {
        closeDialog();
      })
      .catch(() => {
        // User clicked "Back" — keep dialog open
      });
  };

  const selectedCatalogEntry = watchedSKUNumber
    ? skuCatalog[watchedSKUNumber]
    : null;

  return (
    <StyledDialog
      open={open}
      title="Create SKU Configuration"
      onClose={confirmDialogClose}
      maxWidth="md"
      showSubmitButton={true}
      SubmitButtonProps={{
        loading,
        title: "Create SKU Configuration",
        onSubmit: handleSubmit(onSubmit),
      }}
    >
      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="flex-start">
        {/* SKU Number — drives the hardware component list */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="skuNumber"
            control={control}
            rules={{ required: "SKU Number is required" }}
            render={({ field }) => (
              <TextField
                select
                required
                fullWidth
                label="SKU Number"
                value={field.value ?? ""}
                onChange={e => handleSKUNumberChange(e.target.value)}
                error={errors.skuNumber !== undefined}
                helperText={
                  errors.skuNumber?.message ||
                  "Selects hardware component list from catalog"
                }
              >
                {Object.entries(skuCatalog)
                  .filter(([key]) => key !== "000-000")
                  .map(([key, entry]) => (
                    <MenuItem key={key} value={key}>
                      {key} · {entry.productName}
                    </MenuItem>
                  ))}
              </TextField>
            )}
          />
        </Grid>

        {/* Configuration Name — editable label for this deployment template */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="configurationName"
            control={control}
            rules={{ required: "Configuration name is required" }}
            render={({ field }) => (
              <TextField
                fullWidth
                required
                label="Configuration Name"
                {...field}
                value={field.value ?? ""}
                error={errors.configurationName !== undefined}
                helperText={
                  errors.configurationName?.message ||
                  "Descriptive name for this deployment template"
                }
              />
            )}
          />
        </Grid>

        {/* Password */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="password"
            control={control}
            defaultValue=""
            rules={{
              validate: value => {
                if (value && value.length > 0 && value.length < 8) {
                  return "Password must be at least 8 characters";
                }

                return true;
              },
            }}
            render={({ field }) => (
              <TextField
                fullWidth
                label="Password (optional)"
                {...field}
                placeholder="Device password"
                error={
                  !!errors?.password ||
                  !!(
                    field.value &&
                    field.value.length > 0 &&
                    field.value.length < 8
                  )
                }
                helperText={
                  errors?.password?.message ||
                  (field.value &&
                  field.value.length > 0 &&
                  field.value.length < 8
                    ? "Must be at least 8 characters"
                    : "This will be the default credential used when onboarding devices. Can be overridden per onboarding.")
                }
              />
            )}
          />
        </Grid>

        {/* Default IP */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="defaultIp"
            control={control}
            rules={{
              validate: value => {
                if (!value || value.trim() === "") return true;

                const ipv4Regex =
                  /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

                if (!ipv4Regex.test(value.trim())) {
                  return "Invalid IPv4 address format";
                }

                return true;
              },
            }}
            render={({ field }) => (
              <TextField
                fullWidth
                label="Default IP (optional)"
                placeholder="192.168.1.100"
                {...field}
                value={field.value ?? ""}
                error={!!errors.defaultIp}
                helperText={
                  errors.defaultIp?.message ||
                  "Pre-fills device IPs at onboarding"
                }
              />
            )}
          />
        </Grid>

        {/* Enable Recording */}
        <Grid item xs={12}>
          <Controller
            name="hasRecordVideo"
            control={control}
            render={({ field }) => (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">Enable Recording</Typography>
                <Switch
                  checked={field.value ?? true}
                  onChange={e => field.onChange(e.target.checked)}
                />
              </Stack>
            )}
          />
        </Grid>
      </Grid>

      {/* Component model assignments — populated from catalog when SKU is selected */}
      {selectedCatalogEntry && watchedSKUData.length > 0 && (
        <>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {selectedCatalogEntry.productName} —{" "}
            {selectedCatalogEntry.subComponents.length} components,{" "}
            {selectedCatalogEntry.nodeCount} AGX node
            {selectedCatalogEntry.nodeCount !== 1 ? "s" : ""}
          </Typography>
          <Box sx={{ mb: 2 }}>
            {watchedSKUData.map((cfg, index) => {
              const sub = selectedCatalogEntry.subComponents[index];

              if (!sub) return null;

              return (
                <SKUFormItem
                  key={cfg.name + index}
                  index={index}
                  componentName={sub.name}
                  componentType={sub.type}
                  control={control}
                  errors={errors}
                />
              );
            })}
          </Box>
        </>
      )}
    </StyledDialog>
  );
};

export default CreateSKUDialog;
